/**
 * DataProcessor class to handle yaml parsing and project data processing
 */

import { CONSTANTS } from '@js/global'
import { AssetManager } from '@js/loaders'
import type { ProjectData, ActuData } from '@js/types'
import yaml from 'js-yaml'

import PROJECTS_DATA from '@data/projects.yaml?raw'

const SESSION_KEY_PROJECTS = 'app:projectsData'
const SESSION_KEY_ASSETS   = 'app:assets'

export class DataProcessor {
  private assets: AssetManager
  chatbotData: ProjectData | undefined
  data: ProjectData[]

  constructor() {
    this.assets = AssetManager.getInstance() // singleton, same object is accessible anywhere with this call
    this.chatbotData = undefined
    this.data = []
  }

  /* -------------------------------------------------------------------------
  |  P U B L I C   A P I
  |------------------------------------------------------------------------- */

  /**
   * Full initialization for the home page.
   * Registers all assets, parses CSVs, builds ProjectData[].
   * Persists both assets and project data to sessionStorage for other pages.
   */
  async loadProjectData(): Promise<ProjectData[]> {
    this.loadAssets();

    // Import generated JSON (Vite will bundle them)
    const galleryIndex = (await import('@data/generated/gallery-index.json')).default;
    const thumbSizes    = (await import('@data/generated/thumbnail-sizes.json')).default;

    // Parse YAML
    let projects: ProjectData[] = []
    try {
      // Parse le YAML multi-documents (chaque projet est un document)
      projects = yaml.loadAll(PROJECTS_DATA) as ProjectData[]
    } catch (err) {
      console.error('[DataProcessor] YAML parsing failed:', err)
    }

    this.chatbotData = this.extractChatbotData(projects)
    
    this.removeHiddenProjects(projects)//, keywords)

    // Use generated data instead of CSV parsing
    this.assignGalleryFromIndex(galleryIndex, projects);
    this.loadThumbnails(projects)
    this.applyThumbnailSizes(thumbSizes, projects);

    this.splitAtSeparator(projects)

    this.collectAutoAndManualLinks(projects);

    this.data.push(...projects)

    // Persist for other pages (chatroom, etc.)
    this.assets.persist(SESSION_KEY_ASSETS)
    sessionStorage.setItem(SESSION_KEY_PROJECTS, JSON.stringify(this.data))
    console.debug('[DataProcessor] Data and assets persisted to sessionStorage')

    return this.data
  }

  async loadActuData(): Promise<ActuData> {
    const response = await fetch('/data/actualites.yaml') // public/data
    const text = await response.text()
    return yaml.load(text) as ActuData
  }

  async loadStatement(): Promise<string> {
    const response = await fetch('/data/statement.txt') // public/data
    const text = await response.text()
    return text
  }

  async loadCaptchaCodes(): Promise<string[]> {
    const response = await fetch('/data/captcha_codes.txt') // public/data
    const text = await response.text()
    return text
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(line => line.length > 0 && !line.startsWith('#')); // ignore comments
  }


  getChatbotData(): ProjectData | undefined {
    if (this.chatbotData) {
      return this.chatbotData
    } else {
      console.warn('[DataProcessor] Chatbot data not available yet')
      return undefined
    }
  }

  /**
   * Lightweight initialization for pages that don't need project data (e.g. chatroom).
   * Tries to restore assets from sessionStorage first.
   * Falls back to full asset registration if arriving directly on the page.
   */
  loadAssets(): void {
    const restored = this.assets.restore(SESSION_KEY_ASSETS)

    if (restored) {
      // Lazy groups can't be serialized — always re-register them
      this.registerLazyGroups()
    } else {
      // Direct navigation without going through home first
      this.registerEagerGroups()
      this.registerLazyGroups()
    }
  }

  /**
   * Restore project data from sessionStorage.
   * Returns null if not available (direct navigation).
   */
  restoreProjectData(): ProjectData[] | null {
    const raw = sessionStorage.getItem(SESSION_KEY_PROJECTS)
    if (!raw) return null
    const data = JSON.parse(raw) as ProjectData[]
    console.debug('[DataProcessor] Project data restored from sessionStorage')
    return data
  }

  /* -------------------------------------------------------------------------
  |  A S S E T   R E G I S T R A T I O N
  |------------------------------------------------------------------------- */

  /**
   * Eager groups: URLs are plain strings, fully serializable to sessionStorage
   */
  private registerEagerGroups(): void {
    this.registerIcons()
    this.registerThumbnails()
    this.registerModels()
    this.registerMotifs()
  }

  /**
   * Lazy groups: values are () => Promise<string> loader functions.
   * Functions cannot be serialized — always re-register on every page load.
   */
  registerLazyGroups(): void {
    this.registerImageGallery()
    // this.registerTracks()
  }

  private registerIcons(): void {
    const raw = import.meta.glob('@img/icons/*.svg', {
      eager: true,
      import: 'default'
    }) as Record<string, string>
    this.assets.registerGroup('icons', raw, 'eager')
    console.debug("[DataProcessor] 'icons' registered (eager)")
  }

  private registerThumbnails(): void {
    const raw = import.meta.glob('@img/gallery/**/thumb*.{png,gif}', {
      eager: true,
      import: 'default'
    }) as Record<string, string>
    this.assets.registerGroup('thumbnail', raw, 'eager', { keepFullPath: true })
    console.debug("[DataProcessor] 'thumbnail' registered (eager)", this.assets.getKeys('thumbnail'))
  }

  private registerModels(): void {
    const raw = import.meta.glob('@model/*.glb', {
      eager: true,
      import: 'default'
    }) as Record<string, string>
    this.assets.registerGroup('models', raw, 'eager')
    console.debug("[DataProcessor] 'models' registered (eager)")
  }

  private registerMotifs(): void {
    const motifs = import.meta.glob('@img/motifs/*', {
      eager: true,
      import: 'default'
    }) as Record<string, string>
    this.assets.registerGroup('motifs', motifs, 'eager')

    console.debug("[DataProcessor] 'motifs' registered (eager)")
  }

  private registerImageGallery(): void {
    const raw = import.meta.glob(
      ['@img/gallery/**/*', '!@img/gallery/**/thumb*.{png,gif}'],
      { import: 'default' }
    ) as Record<string, () => Promise<string>>
    this.assets.registerGroup('gallery', raw, 'lazy')
    console.debug("[DataProcessor] 'gallery' registered (lazy)")
  }
  

  /* -------------------------------------------------------------------------
  |  P R O J E C T   D A T A   P R O C E S S I N G
  |------------------------------------------------------------------------- */

  private assignGalleryFromIndex(index: Record<string, string[]>, projects: ProjectData[]) {
    // Helper method
    const extractLeadingNumber = (path: string): number | null => {
      const filename = path.split(/[\\/]/).pop() || '';
      const match = filename.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    };
    projects.forEach(proj => {
      const key = proj.name;
      if (key && index[key]) {
        let galleryPaths = index[key].filter(path => 
          !/thumb[_]?2?\.(png|gif|jpg|jpeg)$/i.test(path)
        );

        galleryPaths = galleryPaths.sort((a, b) => {
          const numA = extractLeadingNumber(a);
          const numB = extractLeadingNumber(b);

          // Both have numbers → sort numerically
          if (numA !== null && numB !== null) {
            return numA - numB;
          }

          // One has number, the other doesn't → numbered comes first
          if (numA !== null) return -1;
          if (numB !== null) return 1;

          // Neither has number → keep original order or sort alphabetically
          // (use localeCompare for natural sort, or just return 0 to preserve import order)
          return a.localeCompare(b);
          // Alternative: return 0; // ← if you want to preserve exact filesystem/import order
        });

        proj._imgGallery = galleryPaths;
      }
    });
  }

  private applyThumbnailSizes(sizes: Record<string, {w: number, h: number}>, projects: ProjectData[]) {
    projects.forEach(proj => {
      const name1 = proj.name;

      if (name1 && sizes[name1]) {
        const { w, h } = sizes[name1];
        const reduction = proj.thumbnail?.sizeReduction ?? 100;
        const newW = Math.round(CONSTANTS.css.boxWidth * reduction / 100);
        const newH = Math.round(h * newW / w);
        const totalH = newH 
          + CONSTANTS.css.boxHeightMin 
          + CONSTANTS.css.thumbnailImageMarginTop 
          + CONSTANTS.css.thumbnailImageMarginBottom;

        proj.thumbnail.size = [newW, totalH];

        // console.debug(`[DataProcessor] Applied thumbnail size for "${proj.name}"`, 
        //   "original:", {w, h}, "→ new:", {newW, newH, totalH});
      } else {
        console.warn(`[DataProcessor] No size data for project "${proj.name}"`);
      }
    });
  }

  private collectAutoAndManualLinks(projects: ProjectData[]): void {
    // 1. Build lookup: tag → list of project names that have it
    const tagToProjects = new Map<string, string[]>();

    projects.forEach(proj => {
      if (proj.tags && Array.isArray(proj.tags)) {
        proj.tags.forEach(tag => {
          const normalized = tag.trim().toLowerCase();
          if (!tagToProjects.has(normalized)) {
            tagToProjects.set(normalized, []);
          }
          tagToProjects.get(normalized)!.push(proj.name);
        });
      }
    });

    // 2. For each project → compute final list of linked project names
    projects.forEach(proj => {
      const linked = new Set<string>();

      // A. Auto-links from shared tags
      if (proj.tags) {
        // Force it to be an array — handles string, empty, undefined, wrong types safely
        const tagsArray = Array.isArray(proj.tags)
          ? proj.tags
          : typeof proj.tags === 'string'
            ? proj.tags.split(',').map(t => t.trim()).filter(Boolean)
            : [];

        tagsArray.forEach(tag => {
          if (typeof tag !== 'string' || !tag.trim()) return; // skip invalid entries

          const normalized = tag.trim().toLowerCase();
          const matches = tagToProjects.get(normalized) || [];
          matches.forEach(name => {
            if (name !== proj.name) {
              linked.add(name);
            }
          });
        });
      }
      // B. Manual linkedProjects (already string | string[])
      if (proj.linkedProjects) {
        const manual = Array.isArray(proj.linkedProjects)
          ? proj.linkedProjects
          : [proj.linkedProjects];
        manual.forEach(name => {
          if (name.trim()) {
            linked.add(name.trim());
          }
        });
      }

      // Store final deduplicated list (as array of names/titles)
      proj._computedLinkedNames = Array.from(linked);   // temporary internal field
      console.debug("[DataProcessor] Computed linked names for project:", proj.name, "→", proj._computedLinkedNames);
    });
  }

  private loadThumbnails(projects: ProjectData[]): void {
    projects?.forEach(proj => {
      const folderName = proj.name
      const allKeys = this.assets.getKeys('thumbnail')
      const normal  = allKeys.find(k => k.includes(`/${folderName}/`) && /thumb\.(png|gif)$/i.test(k))
      const hovered = allKeys.find(k => k.includes(`/${folderName}/`) && /thumb[_]?2\.(png|gif)$/i.test(k))

      if (normal) {
        proj.thumbnail.imgNormal  = this.assets.get('thumbnail', normal)
      } else { 
        console.warn(`[DataProcessor] No thumbnail found for folder: '${folderName}', path: ${normal}`)
      }
      if (hovered) {
        proj.thumbnail.imgHovered = this.assets.get('thumbnail', hovered)
      }
    })
  }
  
  private extractChatbotData(projects: ProjectData[]): ProjectData | undefined {
    for (let i = projects.length - 1; i >= 0; i--) {
      const project = projects[i]
      if (!project) continue

      const title    = typeof project.title    === 'string' ? project.title.toLowerCase()    : ''
      const name = typeof project.name === 'string' ? project.name.toLowerCase() : ''

      if (title === 'chatbot' || name === "chatbot") {
        const chatbotData = projects.splice(i, 1)[0]
        console.debug(`[DataProcessor] Extracted chatbot data: '${chatbotData?.name ?? '(no title)'}'`)
        return chatbotData
      }
    }
    console.debug('[DataProcessor] Chatbot data not found')
    return undefined
  }

  private removeHiddenProjects(projects: ProjectData[]): void {
      for (let i = projects.length - 1; i >= 0; i--) {
        const project = projects[i]
        if (project?.hideProject === true) {
          console.debug('[DataProcessor] Removed hidden project:', project.title)
          projects.splice(i, 1)
        }
      }
    }

  private splitAtSeparator(projects: ProjectData[]): void {
    const separators = [' @ ', ' @', '@ ', '@']

    const split = (str: string | string[] | undefined): string | string[] | undefined => {
      if (!str || typeof str !== 'string') return str

      for (const sep of separators) {
        if (str.includes(sep)) return str.split(sep)
      }
      return str
    }

    projects.forEach(project => {
      for (const [key, value] of Object.entries(project)) {
        (project as any)[key] = split(value as any)
      }
    })
  }
}
